import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import BackendService from '../services/backendService';
import { PreferredMimeType } from '../services/backendService/types';
import { useMetrics } from './useMetrics';
import useRouterMatch from './useRouterMatch';

/**
 * What a teacher sees when Drive will not hand the file over. Shared drive admins
 * can stop viewers downloading or copying, and that is the common way to land
 * here — so the message says what to do about it rather than naming a permission.
 */
export const DOWNLOAD_NOT_ALLOWED_MESSAGE =
  "The owner of this shared drive doesn't allow downloads. Ask them for edit access, or make a copy in My Drive.";
export const DOWNLOAD_NOT_ALLOWED_KEY = 'Materials.GoogleDrive.DownloadNotAllowed';

export const isDownloadNotAllowedError = (error: unknown) =>
  (error as Error | null)?.message === DOWNLOAD_NOT_ALLOWED_MESSAGE;

export const useUploadGoogleDrive = (activeTenantId: string) => {
  const client = useQueryClient();
  const metrics = useMetrics();
  const { templateId } = useRouterMatch();
  const {
    mutate: upload,
    isError,
    isPending: isLoading,
    isSuccess,
    reset,
    data,
    error,
  } = useMutation({
    mutationFn: async ({
      fileId,
      preferredMimeType,
    }: {
      fileId: string;
      preferredMimeType?: PreferredMimeType;
      onSuccessFunction?: () => void;
    }) => {
      try {
        const {
          fileUploadId,
          filePath,
          fileType,
        } = await BackendService.instance.integrations.googleDrive.copyFileToBucket(
          fileId,
          activeTenantId,
          preferredMimeType,
        );
        return { fileUploadId, filePath, fileType };
      } catch (err) {
        // The backend answers 403 when Drive says the file can't be downloaded.
        // Callers match on the message, so every surface can say the same thing.
        if ((err as AxiosError).response?.status === 403) {
          throw new Error(DOWNLOAD_NOT_ALLOWED_MESSAGE);
        }
        throw err;
      }
    },

    onSuccess: (data, { onSuccessFunction }) => {
      if (onSuccessFunction) {
        onSuccessFunction();
      }
      const templateCacheKey = ['template', templateId];
      client.invalidateQueries({
        queryKey: templateCacheKey,
      });
      metrics.logEvent('Creator.Upload.GoogleDrive.Generator.Success', { templateId });
    },
  });

  return {
    upload,
    isUploading: isLoading,
    isSuccess: isSuccess,
    isError,
    error,
    fileInfo: data,
    reset,
  };
};
